'use client';

import PriceDetails from '@/components/global/PriceDetails';
import { useProduct } from '@/contexts/ProductContext';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice } from '@/utils/utilities.mjs';

// The "starting at" monthly estimate below is derived from the financing terms
// currently configured in Affirm: orders under $1,000 finance over 12 months,
// orders at or above $1,000 finance over 31 months. If those terms change in
// Affirm, these constants must be updated to match or the displayed monthly
// price will no longer agree with what Affirm actually quotes at checkout.
const AFFIRM_TIER_THRESHOLD = 1000;
const AFFIRM_MONTHS_STANDARD = 31;
const AFFIRM_MONTHS_UNDER_THRESHOLD = 12;

const affirmMonthlyPrice = (price) => {
  if (price <= 0) return 0;
  const divisor =
    price < AFFIRM_TIER_THRESHOLD ? AFFIRM_MONTHS_UNDER_THRESHOLD : AFFIRM_MONTHS_STANDARD;
  return Math.ceil(price / divisor);
};


const AffirmLogo = () => (
  /* eslint-disable-next-line @next/next/no-img-element */
  <img
    src="https://cdn-assets.affirm.com/images/black_logo-transparent_bg.png"
    alt="Affirm"
    className="inline-block h-4.25 w-auto ml-1 align-baseline"
  />
);

const ProductDetailsPrice = ({ block }) => {
  const { selectedVariant, selectedVariantPrices } = useProduct();
  const pricing = usePricing();
  const { show_finance_price } = block ?? {};

  if (!selectedVariant) return null;

  const currencyCode = selectedVariant.price?.currencyCode ?? 'USD';
  const formatDisplayPrice = (amount) => formatPrice(amount, currencyCode);

  const fallbackPrice = parseFloat(selectedVariant.price?.amount ?? 0);
  const price = selectedVariantPrices?.sale ?? fallbackPrice;
  const fullPrice = selectedVariantPrices?.full ?? fallbackPrice;
  const hasDiscount = selectedVariantPrices?.hasPriceDifference && fullPrice > price;
  const promotionalCopy = pricing.promotionalCopy(selectedVariant, 'normal');
  const monthlyPrice = affirmMonthlyPrice(price);

  if (price === 0) return null;

  const pricingDetailsClasses = 'py-1';

  if (show_finance_price) {
    return (
      <div data-name="product_details_price" className="grid grid-cols-2 bg-white text-center">
        {/* Pay Now column */}
        <div className="flex flex-col items-center px-5 py-3">
          <span className="text-sm text-primary">Pay Now</span>
          <span className="text-3xl font-medium text-primary pt-1 pb-1 lg:pb-2">
            {formatDisplayPrice(price)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-primary line-through font-light pb-1">
              {formatDisplayPrice(fullPrice)}
            </span>
          )}
          {hasDiscount && promotionalCopy && (
            <span className="font-worksans font-normal text-xs md:text-sm text-primary">
              {promotionalCopy}
            </span>
          )}
          <PriceDetails className={pricingDetailsClasses} />
        </div>

        {/* Affirm column — divider via border-l */}
        <div className="border-l-2 border-neutral-200 text-center px-5 py-3">
          <span className="text-sm text-primary">Starting at</span>
          <span className="block text-3xl font-medium text-primary pt-1 pb-1 lg:pb-2">
            {formatDisplayPrice(monthlyPrice)}
          </span>
          <span className="block text-center text-sm text-primary">
            /mo or 0% APR with <AffirmLogo />.
          </span>
          <button
            type="button"
            onClick={() => {
              if (typeof window.affirm?.ui?.openModal === 'function') {
                window.affirm.ui.openModal({ amount: Math.round(price * 100), pageType: 'product' });
              } else {
                window.open('https://www.affirm.com/apps/prequal', '_blank', 'noopener,noreferrer');
              }
            }}
            className="underline font-normal text-sm text-sea cursor-pointer"
          >
            See if you qualify
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center bg-white px-5 py-3">
      <span className="text-3xl font-medium text-primary pt-1 pb-1 lg:pb-2">
        {formatDisplayPrice(price)}
      </span>
      {hasDiscount && (
        <span className="text-sm text-primary line-through font-light pb-1">
          {formatDisplayPrice(fullPrice)}
        </span>
      )}
      {hasDiscount && promotionalCopy && (
        <span className="font-worksans font-normal text-xs md:text-sm text-primary">
          {promotionalCopy}
        </span>
      )}
      <PriceDetails className={pricingDetailsClasses} />
    </div>
  );
};

export default ProductDetailsPrice;
